import {Injectable} from '@angular/core';
import {SubventionService} from '../service/subvention.service';
import {isEmpty} from 'lodash';
import {LoanSubventionDetails, Subvention} from '../model/subvention-preference.model';
import {WorstEmiScheduleGenerator} from '@finzy/node-emi-calculator/lib/emiCalculator/WorstEmiScheduleGenerator';
import {EffectiveRoiCalculator} from '@finzy/node-emi-calculator/lib/interest/EffectiveRoiCalculator';
import {Loan} from '../model/loan.model';

@Injectable()
export class SubventionUtil {
    constructor(private subventionService: SubventionService, private worstEmiScheduleGenerator: WorstEmiScheduleGenerator, private effectiveRoiCalculator: EffectiveRoiCalculator) {
    }

    public async isSubvented(loanId: string): Promise<boolean> {
        let subvention: Subvention;

        try {
            subvention = await this.subventionService.getSubvention(loanId);
        } catch (error) {
            if (error.status === 404) {
                return false;
            } else {
                throw error;
            }
        }
        return !isEmpty(subvention);
    }

    public async getSubventionDetailsForLoans(loans: Loan[]): Promise<LoanSubventionDetails[]> {
        const loansSubventionDetails: LoanSubventionDetails[] = [];
        const loansWithPartners = loans.filter(loan => loan.partner);
        if (loansWithPartners && loansWithPartners.length > 0) {
            const loanIdsWithPartners = loansWithPartners.map(loanWithPartner => loanWithPartner._id);
            const subevntionsForLoans = await this.subventionService.getSubventionsForLoans(loanIdsWithPartners);
            for (const subevntionForLoan of subevntionsForLoans) {
                const loan = loansWithPartners.find(loanWithPartner => loanWithPartner._id === subevntionForLoan.loanId);
                const loanSubventionDetails = await this.getIndicativeEffectiveRoiAndEmiForWorstDateScheduleBasedOnSubvention(subevntionForLoan, loan);
                loansSubventionDetails.push(loanSubventionDetails);
            }
        }
        return loansSubventionDetails;
    }

    public async getEffectiveRoiAndEffectiveEmiForSubventedLoan(loanDetails: any): Promise<{ roi: number, emi: number }> {
        const subvention = await this.subventionService.getSubvention(loanDetails._id.toString());
        const loanIndicativeDetails = await this.getIndicativeEffectiveRoiAndEmiForWorstDateScheduleBasedOnSubvention(subvention, loanDetails);
        return loanIndicativeDetails ? {
            roi: loanIndicativeDetails.effectiveRoi,
            emi: loanIndicativeDetails.indicativeEmiSchedule[0].emi_amount
        } : null;
    }

    public async getIndicativeEffectiveRoiAndEmiForWorstDateScheduleBasedOnSubvention(subvention: Subvention, loanDetails: Loan): Promise<LoanSubventionDetails> {
        if (!subvention) {
            return null;
        }
        const emiSchedule = await this.worstEmiScheduleGenerator.generateFor(loanDetails.loan_amount, loanDetails.loan_tenure, loanDetails.roi);
        const totalInterest = emiSchedule.getTotalInterest();
        const subventableAmountByPartner = this.getSubventableAmountForSubventionPercentage(subvention.maxApplicableSubventionPercentage, loanDetails.loan_amount);
        const subventedAmount = Math.min(subventableAmountByPartner, totalInterest);
        const interestToBePaidAfterSubvention = totalInterest - subventedAmount;
        const effectiveRoi = await this.effectiveRoiCalculator.calculateForWorstDate(loanDetails.loan_amount, interestToBePaidAfterSubvention, loanDetails.loan_tenure);
        const effectiveEmiSchedule = await this.worstEmiScheduleGenerator.generateFor(loanDetails.loan_amount, loanDetails.loan_tenure, effectiveRoi);

        return {
            loanId: loanDetails._id,
            effectiveRoi: effectiveRoi,
            indicativeEmiSchedule: effectiveEmiSchedule
        };
    }

    private getSubventableAmountForSubventionPercentage(subventionPercentage: number, loanFulfilledAmount: number): number {
        return Number(((loanFulfilledAmount * subventionPercentage) / 100).toFixed(2));
    }
}
